import React, { useEffect, useMemo, useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Chart } from "chart.js/auto";
import './MainDashboard.css';
import VasorumLogo from '../../images/clientlogos/Vasorum Logo.jpg';



export default function MainDashboard() {
  const {
    user,
    companyId,
    loading,
    error,
    sourceFile,
    mainDashboardObject,
    niceMoney,
    formatNumber,
    niceAsp,
  } = useOutletContext();


  // If you're reusing /api/metrics/portal, you'd add these to metrics on the server.
  const data = useMemo(() => mainDashboardObject|| {}, [mainDashboardObject]);
  console.log(data);

  // Latest Date (found thru a search within data.calendarInfo) \\
  const latestDate = data?.calendarInfo?.find(ci => ci.valueName === "Data Thru")?.value;
  // console.log('Latest Date:', latestDate);

  // Delta Date Previous Qtr \\
  const deltaDatePreviousQtr = data?.calendarInfoPreviousQtr?.find(ci => ci.valueName === "Previous Quarter Delta Date")?.value;
  // console.log('Delta Date Previous Qtr:', deltaDatePreviousQtr);

  // Current Qtr Date Range \\
  const CurrentQtrStart = data?.currentQtr_DateRanges?.start;
  const CurrentQtrEnd = data?.currentQtr_DateRanges?.end;
  // console.log('Current Qtr Start:', CurrentQtrStart);
  // console.log('Current Qtr End:', CurrentQtrEnd);

  // Previous Qtr Date Range \\
  const PreviousQtrStart = data?.previousQtr_DateRanges?.start;
  const PreviousQtrEnd = data?.previousQtr_DateRanges?.end;
  // console.log('Previous Qtr Start:', PreviousQtrStart);
  // console.log('Previous Qtr End:', PreviousQtrEnd);


  // Sales Graph Data Points \\
  const aspGraphDataPoints = data?.aspGraphDataPoints || data?.metrics?.mainDashboard?.aspGraphDataPoints || [];

  // Helper to format numbers into commas for thousands (used in tables with large numbers)
   const commaColumns = new Set([ // add any metric labels (from "territoryKeyByLabel") here whose values should be formatted with commas
    "Total Sales Units",
    "Orders from Existing Accounts",
    "Orders from Base Accounts",
    "Total Orders",
  ]);

  const formatByLabel = (label, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value ?? "";
    return commaColumns.has(label) ? formatNumber(num) : value;
  };

  // Current Qtr Calendar Summary \\
  const calendarRows =
    (data?.calendarInfo || data?.metrics?.mainDashboard?.calendarInfo || []).map(
      ({ valueName, value }) => [valueName, value]
  );
  const totalWeekDaysInQtr = data?.calendarInfo?.find(
    (ci) => ci.valueName === "Total WeekDays in Quarter"
  );
  const elapsedWeekDaysInQtr = data?.calendarInfo?.find(
    (ci) => ci.valueName === "Total WeekDays Elapsed"
  );

  // 1099 Employment Type Stats CURRENT QTR \\
  const all1099Stats = data?.all1099CurrentQtrStats || [];
  const all1099Rows = all1099Stats.map(({ valueName, value }) => [valueName, value]);
  const all1099Names = all1099Stats.map(s => s.valueName);
  const all1099Values = all1099Stats.map(s => formatByLabel(s.valueName, s.value));
  // console.log(all1099Stats);
  // console.log(all1099Names);
  // console.log(all1099Values);

  // Direct/Hybrid Employment Type Stats CURRENT QTR \\
  const allNon1099Stats = data?.allNon1099CurrentQtrStats || [];
  const allNon1099Rows = allNon1099Stats.map(({ valueName, value }) => [valueName, value]);
  const allNon1099Names = allNon1099Stats.map(s => s.valueName);
  const allNon1099Values = allNon1099Stats.map(s => formatByLabel(s.valueName, s.value));
  // console.log(allNon1099Stats);
  // console.log(allNon1099Names);
  // console.log(allNon1099Values);

  // 1099 Employment Type Stats PREVIOUS QTR \\
  const all1099StatsPreviousQtr = data?.all1099PreviousQtrStats || [];
  const all1099RowsPreviousQtr = all1099StatsPreviousQtr.map(({ valueName, value }) => [valueName, value]);
  const all1099NamesPreviousQtr = all1099StatsPreviousQtr.map(s => s.valueName);
  const all1099ValuesPreviousQtr = all1099StatsPreviousQtr.map(s => formatByLabel(s.valueName, s.value));
  // console.log(all1099StatsPreviousQtr);
  // console.log(all1099NamesPreviousQtr);
  // console.log(all1099ValuesPreviousQtr);

  // Direct/Hybrid Employment Type Stats PREVIOUS QTR \\
  const allNon1099StatsPreviousQtr = data?.allNon1099PreviousQtrStats || [];
  const allNon1099RowsPreviousQtr = allNon1099StatsPreviousQtr.map(({ valueName, value }) => [valueName, value]);
  const allNon1099NamesPreviousQtr = allNon1099StatsPreviousQtr.map(s => s.valueName);
  const allNon1099ValuesPreviousQtr = allNon1099StatsPreviousQtr.map(s => formatByLabel(s.valueName, s.value));
  // console.log(allNon1099StatsPreviousQtr);
  // console.log(allNon1099NamesPreviousQtr);
  // console.log(allNon1099ValuesPreviousQtr);
  
  // Total Employment Type Stats CURRENT QTR \\
  const totalStats = data?.totalCurrentQtrStats || [];
  const totalRows = totalStats.map(({ valueName, value }) => [valueName, value]);
  const totalNames = totalStats.map(s => s.valueName);
  const totalValues = totalStats.map(s => formatByLabel(s.valueName, s.value));
  // console.log(totalStats);
  // console.log(totalNames);
  // console.log(totalValues);

  // Total Employment Type Stats PREVIOUS QTR \\
  const totalStatsPreviousQtr = data?.totalPreviousQtrStats || [];
  const totalRowsPreviousQtr = totalStatsPreviousQtr.map(({ valueName, value }) => [valueName, value]);
  const totalNamesPreviousQtr = totalStatsPreviousQtr.map(s => s.valueName);
  const totalValuesPreviousQtr = totalStatsPreviousQtr.map(s => formatByLabel(s.valueName, s.value));
  // console.log(totalStatsPreviousQtr);
  // console.log(totalNamesPreviousQtr);
  // console.log(totalValuesPreviousQtr);

  // Employment Type Rows and Labels CURRENT QTR \\
  const employmentTypeRows = [
    all1099Values,
    allNon1099Values,
    totalValues,
  ];
  // Employment Type Rows and Labels PREVIOUS QTR \\
  const employmentTypeRowsPreviousQtr = [
    all1099ValuesPreviousQtr,
    allNon1099ValuesPreviousQtr,
    totalValuesPreviousQtr,
  ];

  const employmentTypeLabels = ["1099", "Direct/Hybrid", "Total"];

  // By Territory/Sales Rep Stats (Rows & Labels) \\
  const territoryStats = data?.allTerritoriesCurrentQtr || [];
  const territoryKeyByLabel = {
    "Quota": "QuotaValue",
    "Total Sales": "TotalSales",
    "Delta To Quota": "DeltaToQuota",
    "% to Quota": "PercentToQuota",
    "Total Sales Units": "TotalUnits",
    "ASP": "ASP",
    "Run Rate": "RunRate",
    "Run Rate to Quota": "RunRateToQuota",
    "DPA (Devices Per Account)": "DPA",
    "Orders from New Accounts": "OrdersFromNewAccounts",
    "Orders from Existing Accounts": "OrdersFromExistingAccounts",
    "Orders from Base Accounts": "OrdersFromBaseAccounts",
    "Total Orders": "TotalOrders",
    "New Accounts": "NewAccounts",
    "Existing Accounts": "ExistingAccounts",
    "Base Accounts": "BaseAccounts",
    "Reordering Base Accounts": "ReorderingBaseAccounts",
    "Reorder Rate": "ReorderRate",
    "Total Accounts": "TotalAccounts",
    "New Accounts with Reorder(s)": "newAccountsWithReorders",
  };
  const quotaTerritoryStats = data?.quotaTerritoriesCurrentQtrDisplay;
  const nonQuotaTerritoryStats = data?.nonQuotaTerritoriesCurrentQtrDisplay;

  const quotaTerritoryRows = quotaTerritoryStats.map(t =>
    all1099Names.map(label => formatByLabel(label, t[territoryKeyByLabel[label]]))
  );
  const quotaTerritoryRowLabels = quotaTerritoryStats.map(
    t => `${t.SalesReps.join(" & ")}: ${t.Territory}`
  );

  const nonQuotaTerritoryRows = nonQuotaTerritoryStats.map(t =>
    all1099Names.map(label => formatByLabel(label, t[territoryKeyByLabel[label]]))
  );
  const nonQuotaTerritoryRowLabels = nonQuotaTerritoryStats.map(
    t => `${t.SalesReps.join(" & ")}: ${t.Territory}`
  );
  // console.log(territoryStats);
  // console.log(quotaTerritoryStats);
  // console.log(nonQuotaTerritoryStats);
  // console.log('Territory Rows:', quotaTerritoryRows);
  // console.log('Territory Row Labels:', quotaTerritoryRowLabels);
  // console.log('Non-Quota Territory Rows:', nonQuotaTerritoryRows);
  // console.log('Non-Quota Territory Row Labels:', nonQuotaTerritoryRowLabels);

  /* ---------- Aggregate SubTotal Rows into by Territory Datasets Current Qtr (Start) ---------- */
  const quotaTotals = data?.quotaTerritoriesCurrentQtrTotals;
  const nonQuotaTotals = data?.nonQuotaTerritoriesCurrentQtrTotals;

  const buildTerritoryRow = t =>
  all1099Names.map(label => formatByLabel(label, t[territoryKeyByLabel[label]]));

  const quotaTerritoryRowsWithTotal = quotaTotals
  ? [...quotaTerritoryRows, buildTerritoryRow(quotaTotals)]
  : quotaTerritoryRows;

  const nonQuotaTerritoryRowsWithTotal = nonQuotaTotals
  ? [...nonQuotaTerritoryRows, buildTerritoryRow(nonQuotaTotals)]
  : nonQuotaTerritoryRows;

  const quotaTerritoryRowLabelsWithTotal = quotaTotals
  ? [...quotaTerritoryRowLabels, "SubTotal"]
  : quotaTerritoryRowLabels;

const nonQuotaTerritoryRowLabelsWithTotal = nonQuotaTotals
  ? [...nonQuotaTerritoryRowLabels, "SubTotal"]
  : nonQuotaTerritoryRowLabels;
  /* ---------- Aggregate SubTotal Rows into by Territory Datasets Current Qtr (END) ---------- */

  /* ---------- Aggregate SubTotal Rows into by Territory Datasets Previous Qtr (Start) ---------- */
  const quotaTotalsPreviousQtr = data?.quotaTerritoriesPreviousQtrTotals;
  const nonQuotaTotalsPreviousQtr = data?.nonQuotaTerritoriesPreviousQtrTotals;

  // Reuse the same buildTerritoryRow function since the structure is the same

  const quotaTerritoryStatsPreviousQtr = data?.quotaTerritoriesPreviousQtrDisplay || [];
  const nonQuotaTerritoryStatsPreviousQtr = data?.nonQuotaTerritoriesPreviousQtrDisplay || [];

  const quotaTerritoryRowsPreviousQtr = quotaTerritoryStatsPreviousQtr.map(t =>
    all1099Names.map(label => formatByLabel(label, t[territoryKeyByLabel[label]]))
  );

  const nonQuotaTerritoryRowsPreviousQtr = nonQuotaTerritoryStatsPreviousQtr.map(t =>
    all1099Names.map(label => formatByLabel(label, t[territoryKeyByLabel[label]]))
  );

  const quotaTerritoryRowsWithTotalPreviousQtr = quotaTotalsPreviousQtr
  ? [...quotaTerritoryRowsPreviousQtr, buildTerritoryRow(quotaTotalsPreviousQtr)]
  : quotaTerritoryRowsPreviousQtr;

  const nonQuotaTerritoryRowsWithTotalPreviousQtr = nonQuotaTotalsPreviousQtr
  ? [...nonQuotaTerritoryRowsPreviousQtr, buildTerritoryRow(nonQuotaTotalsPreviousQtr)]
  : nonQuotaTerritoryRowsPreviousQtr;

  const quotaTerritoryRowLabelsWithTotalPreviousQtr = quotaTotalsPreviousQtr
  ? [...quotaTerritoryRowLabels, "SubTotal"]  
  : quotaTerritoryRowLabels;

const nonQuotaTerritoryRowLabelsWithTotalPreviousQtr = nonQuotaTotalsPreviousQtr
  ? [...nonQuotaTerritoryRowLabels, "SubTotal"]
  : nonQuotaTerritoryRowLabels;
  /* ---------- Aggregate SubTotal Rows into by Territory Datasets Previous Qtr (END) ---------- */

  /* ---------- Daily Sales Line Graph Current/Previous Qtr (START) ---------- */
  const dailySalesDataPoints =
  data?.dailySalesDataPoints || data?.metrics?.mainDashboard?.dailySalesDataPoints || [];

  const monthlySalesDataPoints =
    data?.monthlySalesDataPoints || data?.metrics?.mainDashboard?.monthlySalesDataPoints || [];
  /* ---------- END Daily Sales Line Graph Current/Previous Qtr (END) ---------- */

  /* ---------- Last 4 Qtr Trends Table (START) ---------- */
  const last4QtrTrendsRowLabels = ['Sales', 'Average Daily Sales', 'Sales Units', 'Average Daily Sales Units', 'Run Rate', 'DPA']

  const last4QtrTrendsTableColumnLabels = data?.last4QtrTrendsTableColumnHeaders.map(l => l.value || "") || [];
  const last4QtrTrendsRowHeaderLabel = last4QtrTrendsTableColumnLabels[0] || "";
  const last4QtrTrendsTableQuarterLabels = last4QtrTrendsTableColumnLabels.slice(1);
  // console.log(last4QtrTrendsTableColumnLabels);

  const last4QtrTrendsTableRows = [
  data?.last4QtrTrendsTableTest.map(r => r.amountTotal), // sales
  data?.last4QtrTrendsTableTest.map(r => r.amountDailyAvg), // average daily sales
  data?.last4QtrTrendsTableTest.map(r => formatNumber(r.qtrTotal)), // sales units
  data?.last4QtrTrendsTableTest.map(r => formatNumber(Math.round(r.qtyDailyAvg))), // average daily sales units
  data?.last4QtrTrendsTableTest.map(r => r.runRate), // run rate
  data?.last4QtrTrendsTableTest.map(r => r.dpa), // dpa
  ];
  /* ---------- END Last 4 Qtr Trends Table (END) ---------- */

  /* ---------- Current Qtr vs Same Qtr Last Year Trends Table (START) ---------- */

  const currentQtrVsLastYearTrendsTableColumnLabels =
    data?.currentQtrToSameQtrLastYearTrendsTableColumnHeaders.map(l => l.value || "") || [];
  const currentQtrVsLastYearRowHeaderLabel = currentQtrVsLastYearTrendsTableColumnLabels[0] || "";
  const currentQtrVsLastYearQuarterLabels = currentQtrVsLastYearTrendsTableColumnLabels.slice(1);
  // console.log(currentQtrVsLastYearTrendsTableColumnLabels);

  const currentQtrVsLastYearTrendsTableRows = [
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => r.amountTotal),
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => r.amountDailyAvg),
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => formatNumber(r.qtrTotal)),
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => formatNumber(Math.round(r.qtyDailyAvg))),
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => r.runRate),
    data?.currentQtrToSameQtrLastYearTrendsTableTest.map(r => r.dpa),
  ];
  /* ---------- END Current Qtr vs Same Qtr Last Year Trends Table (END) ---------- */

  /* ---------- Current Qtr Highlights and Lowlights Table (START) ---------- */
  const companyTrendsHighlights = data?.companyTrendsSummaryRankedHighlights || [];
  const companyTrendsLowlights = data?.companyTrendsSummaryRankedLowlights || [];

  const companyTrendsSummaryTableRows = Array.from(
    { length: Math.max(companyTrendsHighlights.length, companyTrendsLowlights.length) },
    (element, i) => {
      const h = companyTrendsHighlights[i];
      const l = companyTrendsLowlights[i];

      return [
        i + 1,
        h?.valueName ? <strong>{h.valueName}</strong> : "Not populating Metric Name Correctly",
        h?.valueCurrentQtrToPreviousQtr ?? "Not populating Metric Value Correctly",
        h?.valueCurrentQtrToSameQtrLastYr ?? "Not populating Metric Value Correctly",
        l?.valueName ? <strong>{l.valueName}</strong> : "Not populating Metric Name Correctly",
        l?.valueCurrentQtrToPreviousQtr ?? "Not populating Metric Value Correctly",
        l?.valueCurrentQtrToSameQtrLastYr ?? "Not populating Metric Value Correctly",
      ];
    }
  );
  // console.log(companyTrendsSummaryTableRows);

  /* ---------- END Current Qtr Highlights and Lowlights Table (END) ---------- */

  return (
    <main className="dash">
      {/* Page header */}
      <header className="dash__header">
        <img src={VasorumLogo} alt="Logo" className="dash__logo" />
        <div>
          <h1 className="dash__title">{companyId} Main Dashboard</h1>
          <p className="dash__subtitle">
            Welcome, {user?.name || "User"} <br /> Source file: {sourceFile || "—"}
          </p>
        </div>

        <div className="dash__headerActions">
          <Link className="dash__link" to="/portal">← Back to Portal</Link>
          {/* Optional: add refresh button if you later support refetch */}
          {/* <button className="btn" onClick={refetch}>Refresh</button> */}
        </div>
      </header>

      {loading && <p>Loading…</p>}
      {error && <p className="dash__error">{error}</p>}

      {!loading && !error && (
        <>
          {/* =========================
              TOP STRIP (4 cards)
              ========================= */}
          <section className="dash__topGrid">
            <Card title="Current Qtr Calendar Summary">
              <KeyValueTable rows={calendarRows} />
            </Card>

            {/* <Card title="Legend">
              <ul className="dash__legend">
                <li><span className="swatch swatch--blue" />Total Orders across all Accounts</li>
                <li><span className="swatch swatch--green" />Unique Accounts</li>
                <li><span className="no--swatch" />*Invoice and calendar dates may differ due to future entries</li>
                <li><span className="no--swatch" /><span style={{ fontWeight: 'bold' }} >*Base Accounts </span>= Accounts with order(s) from previous 3 Qtrs</li>
                <li><span className="no--swatch" /><span style={{ fontWeight: 'bold' }} >*DPA </span>= Total Units/Total Active Accounts</li>
              </ul>
            </Card> */}

            <Card title="Last 4 Qtr Trends (Company Wide)" className="dash__topTrendCard">
              <SimpleTable
                columns={last4QtrTrendsTableQuarterLabels || []}
                rows={last4QtrTrendsTableRows || []}
                rowLabels={last4QtrTrendsRowLabels || []}
                rowHeaderLabel={last4QtrTrendsRowHeaderLabel}
              />
            </Card>

            <Card title="Current Qtr vs Same Qtr Last Yr (Company Wide)" className="dash__topTrendCard">
              <SimpleTable
                columns={currentQtrVsLastYearQuarterLabels || []}
                rows={currentQtrVsLastYearTrendsTableRows || []}
                rowLabels={last4QtrTrendsRowLabels || []}
                rowHeaderLabel={currentQtrVsLastYearRowHeaderLabel}
              />
            </Card>
          </section>

          {/* =========================
              MID SUMMARY + CHARTS
              ========================= */}
          <section className="dash__band">
            <Card title="Company Trends Summary (Highlights / Lowlights)" className="dash__bandCard dash__bandCard--summary">
              <SimpleTable
                columns={[
                  "#",
                  "Highlights",
                  "Current vs Prev Qtr",
                  "Current vs Same Qtr LY",
                  "Lowlights",
                  "Current vs Prev Qtr",
                  "Current vs Same Qtr LY",
                ]}
                rows={companyTrendsSummaryTableRows || [] }
              />
            </Card>

            <Card title="Trailing 5Q ASP (Company Wide)" className="dash__bandCard dash__bandCard--asp">
              <AspLineChart
                dataPoints={aspGraphDataPoints}
                height={260}
                formatMoney={niceAsp}
              />
            </Card>

            <Card title="Cumulative Sales: Current vs Prior Quarter (Company Wide)" className="dash__bandCard dash__bandCard--cumulative">
              {/* placeholder ??? swap in your chart component later */}
              <DailySalesLineChart
                dataPoints={dailySalesDataPoints}
                height={525}
                formatMoney={niceMoney}
              />
            </Card>

            <Card title="Trailing 5 Qtr Monthly Sales & Units (Company Wide)" className="dash__bandCard dash__bandCard--monthly">
              <div className="dash__chartFill">
                <MonthlySalesUnitsChart
                  dataPoints={monthlySalesDataPoints}
                  height={525}
                  formatMoney={niceMoney}
                />
              </div>
            </Card>
          </section>

          {/* =========================
              BIG TABLE: OFFICIAL TERRITORIES
              ========================= */}
          {/* <section className="dash__section">
            <SectionHeader
              title="Current Qtr vs Same Qtr Last Yr (Official Territories Only)"
              rightNote={data?.calendar?.pctElapsed ? `${data.calendar.pctElapsed} of quarter elapsed` : null}
            />

            <div className="dash__tableWrap">
              <SimpleTable
                stickyHeader
                compact
                columns={data?.officialTerritories?.columns || []}
                rows={data?.officialTerritories?.rows || []}
                // Later: you can pass a cellClassName callback for conditional formatting
                // cellClassName={(r, c, v) => computeCellClass(r, c, v)}
              />
            </div>
          </section> */}

          <Card title="Legend">
            <ul className="dash__legend 2">
              <li><span className="swatch swatch--blue" />Total Orders across all Accounts</li>
              <li><span className="swatch swatch--green" />Unique Accounts</li>
              <li><span className="no--swatch" />*Invoice and calendar dates may differ due to future entries</li>
              <li><span className="no--swatch" /><span style={{ fontWeight: 'bold' }} >Base Accounts </span>= Accounts with order(s) from previous 3 Qtrs</li>
              <li><span className="no--swatch" /><span style={{ fontWeight: 'bold' }} >DPA </span>= Total Units/Total Active Accounts</li>
            </ul>
          </Card>

          {/* =========================
              PERFORMANCE SECTIONS (STACK)
              ========================= */}
          <section className="dash__section dash__section--performance">
            {/*<SectionHeader title="Employment Type Sales Performance" /> */}
            <TwoUpTables
              tableHeader="Employment Type Sales Performance"
              stacked
              leftTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Current Quarter (${CurrentQtrStart} to ${latestDate})`}
              leftColumns={all1099Names || []}
              leftRows={employmentTypeRows || []}
              rightTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Previous Quarter (${PreviousQtrStart} to ${deltaDatePreviousQtr})`}
              rightColumns={all1099Names || []}
              rightRows={employmentTypeRowsPreviousQtr || []}
              allRowLabels={employmentTypeLabels || []}
            />
          </section>

          <section className="dash__section dash__section--performance">
            {/*<SectionHeader title="Territory Sales Performance (Quotas)" /> */}
            <TwoUpTables
              tableHeader="Territory Sales Performance (Quotas)"
              stacked
              leftTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Current Quarter (${CurrentQtrStart} to ${latestDate})`}
              leftColumns={all1099Names || []}
              leftRows={quotaTerritoryRowsWithTotal || []}
              rightTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Previous Quarter (${PreviousQtrStart} to ${deltaDatePreviousQtr})`}
              rightColumns={all1099Names || []}
              rightRows={quotaTerritoryRowsWithTotalPreviousQtr || []}
              allRowLabels={quotaTerritoryRowLabelsWithTotal || []}
            />
          </section>

          <section className="dash__section dash__section--performance">
            {/*<SectionHeader title="Territory Sales Performance (Non-Quotas)" /> */}
            <TwoUpTables
              tableHeader="Territory Sales Performance (Non-Quotas)"
              stacked
              leftTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Current Quarter (${CurrentQtrStart} to ${latestDate})`}
              leftColumns={all1099Names || []}
              leftRows={nonQuotaTerritoryRowsWithTotal || []} 
              rightTitle={`${data?.calendarInfo?.[1]?.value || "N/A"} ${data?.calendarInfo?.[1]?.valueName || "N/A"} in Previous Quarter (${PreviousQtrStart} to ${deltaDatePreviousQtr})`}
              rightColumns={all1099Names || []}
              rightRows={nonQuotaTerritoryRowsWithTotalPreviousQtr || []} 
              allRowLabels={nonQuotaTerritoryRowLabelsWithTotal || []}
            />
          </section>

          {/* <section className="dash__section">
            {/* <SectionHeader title="Territory Sales Performance (House Accounts)" /> */}
            {/* <TwoUpTables
              tableHeader="Territory Sales Performance (House Accounts)"
              leftTitle={`Current Quarter (${data?.calendar?.currentWindow || "—"})`}
              rightTitle={`Previous Quarter (${data?.calendar?.previousWindow || "—"})`}
              leftColumns={data?.houseAccounts?.columns || []}
              leftRows={data?.houseAccounts?.currentRows || []}
              rightColumns={data?.houseAccounts?.columns || []}
              rightRows={data?.houseAccounts?.previousRows || []}
            />
          </section> */}

          {/* <section className="dash__section">
            <SectionHeader title="Manual Hospital Reassignment" />
            <div className="dash__tableWrap">
              <SimpleTable
                stickyHeader
                compact
                columns={data?.manualReassign?.columns || []}
                rows={data?.manualReassign?.rows || []}
              />
            </div>
          </section> */}
        </>
      )}
    </main>
  );
}

/* =========================
   Small presentational bits
   Keep these in-file for now.
   ========================= */

function Card({ title, children, className = "" }) {
  return (
    <div className={`card${className ? ` ${className}` : ""}`}>
      <div className="card__title">{title}</div>
      <div className="card__body">{children}</div>
    </div>
  );
}

function SectionHeader({ title, rightNote }) {
  return (
    <div className="sectionHeader">
      <h2 className="sectionHeader__title">{title}</h2>
      {rightNote ? <div className="sectionHeader__note">{rightNote}</div> : null}
    </div>
  );
}

function KeyValueTable({ rows = [] }) {
  return (
    <table className="kv">
      <tbody >
        {rows.map(([k, v]) => (
          <tr key={k} className="kv-all">
            <td className="kv__k">{k}</td>
            <td className="kv__v">{v ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TwoUpTables({
  tableHeader,
  leftTitle,
  leftColumns,
  leftRows,
  rightTitle,
  rightColumns,
  rightRows,
  allRowLabels,
  stacked = false,
}) {
  if (stacked) {
    return (
      <div className="twoUp twoUp--stacked">
        <SectionHeader title={tableHeader} />
        <div className="twoUp__title">{leftTitle}</div>
        <div className="dash__tableWrap">
          <SimpleTable columns={leftColumns} rows={leftRows} rowLabels={allRowLabels} />
        </div>

        <SectionHeader title={tableHeader} />
        <div className="twoUp__title">{rightTitle}</div>
        <div className="dash__tableWrap">
          <SimpleTable columns={rightColumns} rows={rightRows} rowLabels={allRowLabels} />
        </div>
      </div>
    );
  }

  return (
    <div className="twoUp">
      <SectionHeader title={tableHeader} />
      <SectionHeader title={tableHeader} />
      <div className="twoUp__header">
        <div className="twoUp__title">{leftTitle}</div>
        <div className="twoUp__title">{rightTitle}</div>
      </div>
      <div className="twoUp__col">
        <div className="dash__tableWrap">
          <SimpleTable columns = {leftColumns} rows={leftRows} rowLabels={allRowLabels} />
        </div>
      </div>

      <div className="twoUp__col">
        <div className="dash__tableWrap">
          <SimpleTable columns = {rightColumns} rows={rightRows} rowLabels={allRowLabels} />
        </div>
      </div>
    </div>
  );
}

function SimpleTable({ columns = [], rows = [], rowLabels = [], rowHeaderLabel = "", ...props }) {
  const hasRowLabels = rowLabels.length > 0;

  return (
    <table className="tbl" {...props}>
      <thead>
        <tr>
          {hasRowLabels && <th>{rowHeaderLabel}</th>}
          {columns.map((c, i) => <th key={i}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length + (hasRowLabels ? 1 : 0)}>No rows</td></tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i}>
              {hasRowLabels && <th scope="row">{rowLabels[i] ?? ""}</th>}
              {r.map((cell, j) => <td key={j}>{cell ?? "—"}</td>)}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}


function AspLineChart({ dataPoints = [], height = 240, formatMoney }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || dataPoints.length === 0) return;

    const labels = dataPoints.map(p =>
      String(p.quarterLabel ?? "").replace(" Q", "-Q")
    );
    const values = dataPoints.map(p => {
      const num = Number(p.asp);
      return Number.isFinite(num) ? num : null;
    });

    const formatCurrency = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return formatMoney ? formatMoney(num) : `$${num.toFixed(2)}`;
    };

    // Chart.js doesn’t draw point labels by default. Here’s a simple plugin to do that.
    const pointLabelPlugin = {
      id: "pointLabel",
      afterDatasetsDraw(chart, args, opts) {
        const { ctx } = chart;
        chart.getDatasetMeta(0).data.forEach((pt, i) => {
          const value = chart.data.datasets[0].data[i];
          if (value == null) return;
          const { x, y } = pt.tooltipPosition();

          ctx.save();
          ctx.fillStyle = opts?.color || "#2b2b2b";
          ctx.font = "12px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(formatCurrency(value), x, y - (opts?.offset ?? 6));
          ctx.restore();
        });
      },
    };

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      plugins: [pointLabelPlugin],
      data: {
        labels,
        datasets: [
          {
            label: "ASP",
            data: values,
            borderColor: "#0b5e83",
            backgroundColor: "rgba(11, 94, 131, 0.15)",
            pointBackgroundColor: "#0b5e83",
            pointRadius: 3,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => `ASP: ${formatCurrency(context.parsed.y)}`,
            },
          },
        },
        scales: {
          y: {
            suggestedMin: 190,
            suggestedMax: 205,
            ticks: { callback: (value) => formatCurrency(value) },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [dataPoints, formatMoney]);

  if (dataPoints.length === 0) {
    return <ChartPlaceholder height={height} label="No ASP data available" />;
  }

  return (
    <div style={{ height, position: "relative" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

function DailySalesLineChart({ dataPoints = [], height = 240, formatMoney }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || dataPoints.length === 0) return;

    const labels = dataPoints.map(p => p.businessDaysElapsed ?? "");
    const currentCumulative = dataPoints.map(p => {
      const num = Number(p.currentQtrCumulativeSales);
      return Number.isFinite(num) ? num : null;
    });
    const previousCumulative = dataPoints.map(p => {
      const num = Number(p.previousQtrCumulativeSales);
      return Number.isFinite(num) ? num : null;
    });

    const formatCurrency = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return formatMoney ? formatMoney(num) : `$${num.toFixed(2)}`;
    };

    const labelEveryXSpaces = {
      id: "labelEveryXSpaces",
      afterDatasetsDraw(chart, args, opts) {
        const { ctx, chartArea } = chart;
        const step = opts?.step ?? 5;
        const padding = opts?.padding ?? 3;
        const bg = opts?.background ?? "rgba(255,255,255,0.9)";
        const border = opts?.border ?? "rgba(0,0,0,0.1)";

        const boxes = [];

        const placeLabel = (x, y, text, color, pointX, pointY, force = false) => {
          const metrics = ctx.measureText(text);
          const w = metrics.width + padding * 2;
          const h = 12 + padding * 2;

          const box = {
            x: x - w / 2,
            y: y - h / 2,
            w,
            h,
          };

          if (force) {
            // clamp box into chart area
            if (box.x < chartArea.left) box.x = chartArea.left;
            if (box.x + box.w > chartArea.right) box.x = chartArea.right - box.w;
            if (box.y < chartArea.top) box.y = chartArea.top;
            if (box.y + box.h > chartArea.bottom) box.y = chartArea.bottom - box.h;

            // re-center text after clamping
            x = box.x + box.w / 2;
            y = box.y + box.h / 2;
          } else {
            if (
              box.x < chartArea.left ||
              box.x + box.w > chartArea.right ||
              box.y < chartArea.top ||
              box.y + box.h > chartArea.bottom
            ) {
              return false;
            }
          }

          // avoid overlaps with other labels
          for (const b of boxes) {
            const overlap =
              box.x < b.x + b.w &&
              box.x + box.w > b.x &&
              box.y < b.y + b.h &&
              box.y + box.h > b.y;
            if (overlap) return false;
          }

          // draw background box
          ctx.save();
          ctx.fillStyle = bg;
          ctx.strokeStyle = border;
          ctx.lineWidth = 1;
          ctx.fillRect(box.x, box.y, box.w, box.h);
          ctx.strokeRect(box.x, box.y, box.w, box.h);

          // pointer (callout)
          const anchorX = Math.min(Math.max(pointX, box.x + 6), box.x + box.w - 6);
          const anchorY = pointY > y ? box.y + box.h : box.y; // bottom or top edge
          const tipY = pointY > y ? anchorY + 6 : anchorY - 6;

          ctx.beginPath();
          ctx.moveTo(anchorX - 6, anchorY);
          ctx.lineTo(anchorX + 6, anchorY);
          ctx.lineTo(anchorX, tipY);
          ctx.closePath();
          ctx.fillStyle = bg;
          ctx.strokeStyle = border;
          ctx.fill();
          ctx.stroke();

          // line from pointer to data point (optional but helps)
          ctx.beginPath();
          ctx.moveTo(anchorX, tipY);
          ctx.lineTo(pointX, pointY);
          ctx.strokeStyle = border;
          ctx.stroke();

          // draw text
          ctx.fillStyle = color;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "12px sans-serif";
          ctx.fillText(text, x, y);
          ctx.restore();

          boxes.push(box);
          return true;
        };

        chart.data.datasets.forEach((ds, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((pt, i) => {
            const day = Number(chart.data.labels[i]);
            
            const isLast = i === chart.data.labels.length - 1;
            if (
              !isLast && // on the last day
              (!day || day % step !== 0) // on every xth day
            ) return;

            const value = ds.data[i];
            if (value == null) return;

            const label = formatCurrency(value);
            const { x, y } = pt.tooltipPosition();

            // try multiple positions to avoid collisions ( they get tried in order. negative = left/up, positive = right/down )
            const getOffsets = (datasetIndex) =>
            datasetIndex === 0
              ? { xOffsets: [15, -19, 29, -31], yOffsets: [-32, -16, 16, 32] } // for the first dataset (Current Qtr Cumulative Sales) (Assumes Current Qtr is HIGHER than Previous Qtr)
              : { xOffsets: [-15, 19, -29, 31], yOffsets: [32, 16, -16, -32] }; // for the second dataset (Previous Qtr Cumulative Sales) (Assumes Previous Qtr is LOWER than Current Qtr)

            const { xOffsets, yOffsets } = getOffsets(datasetIndex);

            let placed = false
            const force = isLast;
            for (const xo of xOffsets) {
              for (const yo of yOffsets) {
                if (placeLabel(x + xo, y + yo, label, ds.borderColor || "#2b2b2b", x, y, force)) {
                  placed = true;
                  break; // stops inner loop upon placing a label
                };
              }
              if (placed) break; // stops outer loop upon placing a label
            }
          });
        });
      },
    };



    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Current Qtr Cumulative Sales",
            data: currentCumulative,
            borderColor: "#0b5e83",
            backgroundColor: "rgba(11, 94, 131, 0.12)",
            pointRadius: 2,
            tension: 0.25,
          },
          {
            label: "Previous Qtr Cumulative Sales",
            data: previousCumulative,
            borderColor: "#a23b72",
            backgroundColor: "rgba(162, 59, 114, 0.12)",
            pointRadius: 2,
            tension: 0.25,
          },
        ],
      },
      plugins: [labelEveryXSpaces],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          labelEveryXSpaces: { step: 5, offset: 5}, // check afterDatasetsDraw() for default values of options
          legend: { display: true, position: "top" },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
            },
          },
        },
        scales: {
          y: {
            ticks: { callback: (value) => formatCurrency(value) },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [dataPoints, formatMoney]);

  if (dataPoints.length === 0) {
    return <ChartPlaceholder height={height} label="No daily sales data available" />;
  }

  return (
    <div style={{ height, position: "relative" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

export function MonthlySalesUnitsChart({ dataPoints = [], height = 240, formatMoney }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || dataPoints.length === 0) return;

    const orderedPoints = dataPoints.slice();
    const labels = orderedPoints.map((p) => {
      const parts = String(p.monthLabel ?? "").split("-");
      const monthNumber = Number(parts[1]);
      if (!Number.isFinite(monthNumber)) return p.monthLabel ?? "";
      const monthIndexInQuarter = ((monthNumber - 1) % 3) + 1;
      return `M${monthIndexInQuarter}`;
    });

    const quarterLabels = orderedPoints.map(p => p.quarterLabel ?? "");
    const uniqueQuarters = [];
    quarterLabels.forEach((label) => {
      if (label && !uniqueQuarters.includes(label)) uniqueQuarters.push(label);
    });

    const quarterPalette = [
      "#e07a2c",
      "#1f7a3b",
      "#c62828",
      "#b05ab3",
      "#0e3c91",
    ];
    const quarterColorMap = new Map(
      uniqueQuarters.map((label, index) => [
        label,
        quarterPalette[index % quarterPalette.length],
      ])
    );
    const barColors = quarterLabels.map(
      (label) => quarterColorMap.get(label) || "#6b6b6b"
    );

    const salesValues = orderedPoints.map((p) => {
      const num = Number(p.sales);
      return Number.isFinite(num) ? num : null;
    });
    const unitValues = orderedPoints.map((p) => {
      const num = Number(p.units);
      return Number.isFinite(num) ? num : null;
    });

    const formatCurrency = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return formatMoney ? formatMoney(num) : `$${num.toFixed(2)}`;
    };

    const formatUnits = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return Math.round(num).toLocaleString("en-US");
    };

    const valueLabelPlugin = {
      id: "valueLabel",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          if (dataset.type !== "line") return; // skip Sales bars labels

          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((point, i) => {
            const value = dataset.data[i];
            if (value == null) return;

            const { x, y } = point.tooltipPosition();
            const isLine = dataset.type === "line";
            const label = isLine ? formatUnits(value) : formatCurrency(value);

            let textY = y - (isLine ? 10 : 6);
            let textBaseline = "bottom";

            if (!isLine) {
              const barTop = typeof point.y === "number" ? point.y : y;
              const barBase = typeof point.base === "number" ? point.base : y;
              textY = (barTop + barBase) / 2;
              textBaseline = "middle";
            }

            ctx.save();
            ctx.fillStyle = isLine ? "#1f1f1f" : "#2b2b2b";
            ctx.font = isLine ? "11px sans-serif" : "10px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = textBaseline;
            ctx.fillText(label, x, textY);
            ctx.restore();
          });
        });
      },
    };

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: "Sales",
            data: salesValues,
            backgroundColor: barColors,
            borderColor: barColors,
            borderWidth: 1,
            yAxisID: "y",
          },
          {
            type: "line",
            label: "Units",
            data: unitValues,
            borderColor: "#1f1f1f",
            backgroundColor: "rgba(31, 31, 31, 0.15)",
            pointRadius: 2,
            tension: 0.25,
            yAxisID: "y1",
          },
        ],
      },
      plugins: [valueLabelPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            display: true,
            onClick: () => {},
            labels: {
              generateLabels: () =>
                uniqueQuarters.map((label) => ({
                  text: String(label).replace(" Q", "-Q"),
                  fillStyle: quarterColorMap.get(label),
                  strokeStyle: quarterColorMap.get(label),
                  lineWidth: 1,
                  hidden: false,
                  datasetIndex: 0,
                })),
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const index = items?.[0]?.dataIndex ?? 0;
                const monthLabel = orderedPoints[index]?.monthLabel;
                const quarterLabel = orderedPoints[index]?.quarterLabel;
                return [monthLabel, quarterLabel].filter(Boolean).join(" | ");
              },
              label: (context) => {
                if (context.dataset.type === "line") {
                  return `Units: ${formatUnits(context.parsed.y)}`;
                }
                return `Sales: ${formatCurrency(context.parsed.y)}`;
              },
            },
          },
        },
        scales: {
          y: {
            ticks: { callback: (value) => formatCurrency(value) },
          },
          y1: {
            position: "right",
            grid: { drawOnChartArea: false },
            ticks: { callback: (value) => formatUnits(value) },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [dataPoints, formatMoney]);

  if (dataPoints.length === 0) {
    return (
      <ChartPlaceholder height={height} label="No monthly sales data available" />
    );
  }

  return (
    <div style={{ height, position: "relative" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}


function ChartPlaceholder({ height = 240, label }) {
  return (
    <div className="chartPh" style={{ height }}>
      <div className="chartPh__label">{label}</div>
    </div>
  );
}


